import React, { useState } from 'react';
import MoodWizard from './MoodWizard';
import '../styles/FeelingAdventurous.css';

// Each question answered in the wizard adds the next color from this palette to the card's
// background, so the card subtly "comes alive" as you build up a pick - purely decorative.
const MOOD_PALETTE = ['#ff6b6b', '#feca57', '#48dbfb', '#1dd1a1', '#a29bfe', '#ff9ff3', '#54a0ff', '#f368e0'];

// Preset spots for blobs to land in, cycled through so multiple colors don't all stack in one corner.
const BLOB_SPOTS = [
  { top: '-15%', left: '0%' },
  { top: '45%', left: '65%' },
  { top: '65%', left: '10%' },
  { top: '5%', left: '75%' },
  { top: '50%', left: '35%' },
  { top: '-20%', left: '45%' },
  { top: '30%', left: '85%' },
  { top: '75%', left: '55%' },
];

const FeelingAdventurous = ({ apiBaseUrl, posterPool = [] }) => {
  const [mode, setMode] = useState(null); // null | 'adventure' | 'surprise'
  const [moodColors, setMoodColors] = useState([]);

  const images = posterPool
    .map((item) => item.vertical_image || item.horizontal_image)
    .filter(Boolean);
  // Duplicated so the CSS scroll animation can loop seamlessly.
  const loopImages = images.length > 0 ? [...images, ...images] : [];

  // The very first blob appears the moment the wizard opens, not only once a question is answered.
  const startMode = (next) => {
    setMoodColors([MOOD_PALETTE[0]]);
    setMode(next);
  };

  const closeWizard = () => {
    setMode(null);
    setMoodColors([]);
  };

  const addMoodColor = () => {
    setMoodColors((prev) => [...prev, MOOD_PALETTE[prev.length % MOOD_PALETTE.length]]);
  };

  return (
    <div>
      <h2>Not sure what's next?</h2>
      <div className="feeling-adventurous-card">
        {moodColors.length > 0 && (
          <div className="feeling-adventurous-blobs" aria-hidden="true">
            {moodColors.map((color, i) => {
              const lgSpot = BLOB_SPOTS[i % BLOB_SPOTS.length];
              const smSpot = BLOB_SPOTS[(i + 3) % BLOB_SPOTS.length];
              const duration = 10 + (i % 4) * 2;
              return (
                <React.Fragment key={i}>
                  <span
                    className="feeling-adventurous-blob feeling-adventurous-blob-lg"
                    style={{ background: color, top: lgSpot.top, left: lgSpot.left, animationDuration: `1.6s, ${duration}s`, animationDelay: `0s, ${-i * 2}s` }}
                  />
                  <span
                    className="feeling-adventurous-blob feeling-adventurous-blob-sm"
                    style={{ background: color, top: smSpot.top, left: smSpot.left, animationDuration: `1.6s, ${duration - 3}s`, animationDelay: `0s, ${-i * 3}s` }}
                  />
                </React.Fragment>
              );
            })}
          </div>
        )}
        <div className={`feeling-adventurous-scroller${mode ? ' feeling-adventurous-scroller-collapsed' : ''}`} aria-hidden="true">
          {loopImages.length > 0 && (
            <div className="feeling-adventurous-track">
              {loopImages.map((src, i) => (
                <img key={i} src={src} alt="" loading="lazy" />
              ))}
            </div>
          )}
        </div>
        <div className="feeling-adventurous-info">
          {!mode && (
            <>
              <p>
                Answer a few quick questions about your mood, and we'll dig through your entire library
                to find something that actually fits right now, no endless scrolling required. Or skip
                straight to a fast random pick if you just want a surprise.
              </p>
              <div className="feeling-adventurous-buttons">
                <button type="button" className="btn btn-primary" onClick={() => startMode('adventure')}>
                  <img className="icon" src="/static/icons/compass-solid.svg" alt="" /> Feeling Adventurous?
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => startMode('surprise')}>
                  <img className="icon" src="/static/icons/rocket-solid.svg" alt="" /> Surprise Me
                </button>
              </div>
            </>
          )}
          {mode && <MoodWizard apiBaseUrl={apiBaseUrl} mode={mode} onClose={closeWizard} onAnswer={addMoodColor} />}
        </div>
      </div>
    </div>
  );
};

export default FeelingAdventurous;
